import React from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * Add-API-Data-Source dialog. Extracted from ChatbotDetailPage.jsx (iter132.2).
 * Presentational only — parent owns state, submit handler, and the
 * "See full curl + JSON walkthrough" help modal (opened via
 * ``onOpenHelp``).
 */
export const AddAPISourceDialog = ({
  open,
  onOpenChange,
  newAPISource,
  setNewAPISource,
  onSubmit,
  onOpenHelp,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-white border-slate-200 max-w-lg">
      <DialogHeader>
        <DialogTitle className="text-slate-900">Add API Data Source</DialogTitle>
        <p className="text-xs text-slate-500 mt-1">
          Connect any REST API. The AI fetches fresh data from the endpoint and can answer questions about it.
        </p>
      </DialogHeader>

      {/* Inline sample + use cases */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 space-y-2" data-testid="api-source-sample">
        <div className="font-semibold">Quick example</div>
        <pre className="bg-white border border-blue-100 rounded p-2 text-[11px] text-slate-700 overflow-x-auto font-mono">
{`# A typical product-catalog API
GET https://api.yourstore.com/v1/products
→ [
    { "sku": "TSHIRT-RED-M", "name": "Red T-Shirt (M)",
      "price": 24.99, "in_stock": 142 },
    ...
  ]`}
        </pre>
        <div className="font-semibold pt-1">Great for…</div>
        <ul className="list-disc list-inside space-y-0.5 text-blue-800">
          <li><strong>Real-time inventory / pricing</strong> — &ldquo;Do you have the red M t-shirt in stock?&rdquo;</li>
          <li><strong>Order status</strong> — &ldquo;Where is order #12345?&rdquo; fetching from your order API</li>
          <li><strong>Booking availability</strong> — pull free slots from your scheduling API</li>
          <li><strong>Knowledge that changes hourly</strong> — pricing, FX rates, content from your CMS</li>
        </ul>
        <button
          type="button"
          onClick={onOpenHelp}
          className="text-blue-700 underline hover:text-blue-900"
          data-testid="api-source-help-link"
        >
          See full curl + JSON walkthrough →
        </button>
      </div>

      <div className="space-y-4 py-4">
        <div>
          <Label className="text-slate-600">Source Name</Label>
          <Input
            value={newAPISource.name}
            onChange={(e) => setNewAPISource({ ...newAPISource, name: e.target.value })}
            placeholder="Product Catalog API"
            className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 mt-1"
          />
        </div>
        <div>
          <Label className="text-slate-600">Endpoint URL</Label>
          <Input
            value={newAPISource.endpoint_url}
            onChange={(e) => setNewAPISource({ ...newAPISource, endpoint_url: e.target.value })}
            placeholder="https://api.example.com/products"
            className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 mt-1"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            Must return JSON. Authentication headers can be added after save.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-600">Method</Label>
            <Select
              value={newAPISource.method}
              onValueChange={(v) => setNewAPISource({ ...newAPISource, method: v })}
            >
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="GET" className="text-slate-700">GET</SelectItem>
                <SelectItem value="POST" className="text-slate-700">POST</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-600">Refresh Interval (min)</Label>
            <Input
              type="number"
              value={newAPISource.refresh_interval_minutes}
              onChange={(e) => setNewAPISource({
                ...newAPISource,
                refresh_interval_minutes: parseInt(e.target.value, 10) || 60,
              })}
              className="bg-white border-slate-300 text-slate-900 mt-1"
            />
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          className="border-slate-300 text-slate-700"
        >
          Cancel
        </Button>
        <Button onClick={onSubmit} className="bg-blue-600 hover:bg-blue-700">
          Add API Source
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default AddAPISourceDialog;
